import { Tooltip, Typography, Button, Box, CircularProgress } from '@mui/material';
import Dialog from 'components/dialog';
import { useAccountState } from 'contexts/account/account-context';
import { Trash2 } from 'lucide-react';
import Mui from 'materials';
import React, { PropsWithChildren, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useCreateScenario } from 'views/bluviu-nutrition/features/scenarios/hooks/use-create-scenario';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';
import { ScenariosParams } from 'views/bluviu-nutrition/features/scenarios/types/scenarios-params.type';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';

type Props = PropsWithChildren & {
  button?: React.ReactElement;
  scenario: {
    name: string;
    id: string;
    state: ScenarioState;
  };
};

const DeleteScenarioDialog: React.FC<Props> = ({ scenario, button }) => {
  const { deleteScenario } = useCreateScenario();
  const { selectedSite } = useSelectedSite();
  const queryClient = useQueryClient();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const params = useParams<ScenariosParams>();
  const {
    account: { company },
  } = useAccountState();

  const handleDeleteScenario = () => {
    deleteScenario.mutate(
      { globalCompanyId: company!.companyId, scenarioId: scenario.id },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({
            queryKey: ['getScenarios', params.globalSiteId]
          });
          dialogRef.current?.setOpen(false);
        },
      }
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'xs'}
      title={`Delete scenario (${scenario.name})`}
      button={
        button || (
          <Tooltip title={'Delete'}>
            <Mui.IconButton disabled={scenario.state === ScenarioState.SUBMITTED}>
              <Trash2 size={22} />
            </Mui.IconButton>
          </Tooltip>
        )
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography fontSize={14}>From site: {selectedSite?.name}</Typography>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <Box>
            <FormattedMessage id="general.deleteComment" />
          </Box>
          <Box display={'flex'} justifyContent={'end'} gap={4}>
            <Button variant="outlined" type="submit" size="small" onClick={() => dialogRef.current?.close()}>
              <FormattedMessage id="general.cancel" />
            </Button>
            <Button
              onClick={() => handleDeleteScenario()}
              disabled={deleteScenario.isPending}
              startIcon={deleteScenario.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.yesDelete" />
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default React.memo(DeleteScenarioDialog);
